import {
  DEFAULT_SLIDE_MASTER,
  ImageWithMetadata,
  Presentation,
  Slide,
  TextProps,
} from '@features/powerpoint';
import { titleSlide } from '@features/lci-report/slides';
import { ExtendedFormSchema } from '../components/form-schema';
import { sectionTitleSlide } from '@features/lci-report/slides/shared/section-title-slide';
import { createSharedPresentationConfig } from '@features/lci-report/utils/shared-presentation-config';
import { contentSlideHeader } from '@features/lci-report/utils';
import { overviewSlideInterface } from '../../../slides/overview-slide-interface';
import { formatters } from '@utils/formattersV2';
import { Campaign, CpgReport } from '@features/campaigns';
import { salesLiftSummarySlide } from '../slides/sales-lift-summary-slide';
import { retailersSummarySlide } from '../slides/retailers-summary-slide';
import { sortBy } from '@utils/arrays';
import { dimensionSlide, MinimalCpgDimension } from '../slides/dimension-slide';
import { groupRetailers } from '@features/campaigns/subdomains/cpg';
import {
  dimensionSlides,
  DimensionSlidesProps,
} from '../slides/dimension-slides';
import { howWeDoItSlide } from '../slides/how-we-do-it-slide';
import { lciAdaptiveSyntheticControlSlide } from '../slides/lci-adaptive-synthetic-control-slide';
import { multiTouchAttributionSlide } from '../slides/multi-touch-attribution-slide';
import { projectedSalesMethodologySlide } from '../slides/projected-sales-methodology-slide';
import { minimumsSlide } from '../slides/minimums-slide';
import { talkToUsSlide } from '@features/lci-report/slides/appendix/talk-to-us/slide';
import { emailSlide } from '@features/lci-report/slides/appendix/email/slide';
import { glossarySlides } from '@features/lci-report/slides/appendix/glossary/slides';
import { GLOSSARY } from '@features/glossary/subdomains/cpg';
import { calculationsSlide } from '../slides/calculations-slide';
import {
  NEWLINE_REGEX,
  parsedTakeaways,
} from '@features/lci-report/domain/key-takeaways';
import { validDimensionSlug } from '@features/reports';
import { performanceByTopRetailersSlides } from '../slides/performance-by-top-retailers-slides';
import { buildOverallRetailer } from '../domain/build-overall-retailer';

export type CpgLciReportsFieldsData = {
  brandLogo: ImageWithMetadata | null;
  campaign: Pick<Campaign, 'id' | 'name' | 'startDate' | 'endDate' | 'adSpend'>;
  selectedReportDate: string;
  report: Pick<
    CpgReport,
    | 'impressions'
    | 'matchedForAttribution'
    | 'uniqueUsers'
    | 'retailerBanners'
    | 'totalProjectedSales'
    | 'projectedIncrementalSales'
    | 'incrementalSalesLift'
    | 'averagePricePerTransaction'
    | 'averageTotalBasketAmount'
    | 'penetration'
    | 'totalSalesRate'
    | 'averagePurchaseFrequency'
    | 'incrementalSalesLiftConfidenceLevel'
    | 'existingBuyerPercentage'
    | 'newBuyerPercentage'
    | 'existingBuyerSales'
    | 'newBuyerSales'
    | 'aiInsights'
  > & {
    dimensions: (MinimalCpgDimension &
      Pick<
        CpgReport['dimensions'][number],
        'statisticallySignificant' | 'dimensionName'
      >)[];
    retailers: Pick<
      CpgReport['retailers'][number],
      | 'name'
      | 'projectedIncrementalSales'
      | 'incrementalSalesLift'
      | 'totalProjectedSales'
      | 'averagePricePerTransaction'
      | 'penetration'
      | 'averagePurchaseFrequency'
      | 'salesRate'
      | 'averageTotalBasketAmount'
      | 'salesLiftIndex'
      | 'statisticallySignificant'
    >[];
  };
  roasData: {
    iroas: number | null;
    roas: number | null;
  };
};

export type BuildPresentationProps = {
  data: CpgLciReportsFieldsData;
  customFields: ExtendedFormSchema;
  features: Pick<Settings['features'], 'cpgSalesRate'>;
};

const buildSlide = (texts: TextProps[], slideMaster: string): Slide => {
  return {
    slideMaster,
    texts,
  };
};

const buildPresentationV1 = (props: BuildPresentationProps): Presentation => {
  const { data, features } = props;

  const sharedConfig = createSharedPresentationConfig({
    campaign: data.campaign,
    brandLogo: data.brandLogo,
  });

  const executiveSummaryTexts = {
    header: 'Sales Attribution',
    summary: props.customFields.summary,
  };

  const topPerformerSlideHeader =
    props.customFields.dimensions.Top.tableHeader.length > 0
      ? props.customFields.dimensions.Top.tableHeader
      : 'Top Performing Dimensions';

  const bottomPerformerSlideHeader =
    props.customFields.dimensions.Bottom.tableHeader.length > 0
      ? props.customFields.dimensions.Bottom.tableHeader
      : 'Bottom Performing Dimensions';

  const topDimensions = props.data.report.dimensions.toSorted(
    sortBy('salesLiftIndex', 'desc')
  );
  const bottomDimensions = props.data.report.dimensions
    .filter(dimension => dimension.statisticallySignificant)
    .toSorted(sortBy('salesLiftIndex', 'asc'));
  const overallDimension: MinimalCpgDimension = buildOverallRetailer(
    props.data.report
  );

  const dimensionExcludedColumns: DimensionSlidesProps['excludedColumns'] =
    features.cpgSalesRate ? [] : ['salesRate'];

  const groupedRetailers = groupRetailers(data.report.retailers);

  return {
    ...sharedConfig,
    filename: `${data.campaign.name}_lci_report_${data.selectedReportDate}.pptx`,
    slides: [
      titleSlide({
        id: data.campaign.id,
        name: data.campaign.name,
      }),
      ...(props.customFields.includedSlides.overall.overview
        ? [
            overviewSlideInterface({
              flightDates: [data.campaign.startDate, data.campaign.endDate],
              measurementDates: [
                data.campaign.startDate,
                data.selectedReportDate,
              ],
              metrics: [
                {
                  name: 'Impressions',
                  value: formatters.number(data.report.impressions, {
                    full: true,
                  }),
                },
                {
                  name: 'Users Matched for Attribution',
                  value: formatters.number(data.report.matchedForAttribution, {
                    full: true,
                  }),
                },
                {
                  name: 'Unique Users',
                  value: formatters.number(data.report.uniqueUsers, {
                    full: true,
                  }),
                },
                {
                  name: 'Unique Retailer Banners',
                  value: formatters.number(data.report.retailerBanners, {
                    full: true,
                  }),
                },
                {
                  name: 'Media Spend',
                  value: formatters.currency(data.campaign.adSpend, {
                    full: true,
                  }),
                },
              ],
              reportsIncluded: ['Sales Attribution', 'Performance by Retailer'],
              reportingDimensions: data.report.dimensions.map(
                dimension => dimension.dimensionName
              ),
              slideMaster: DEFAULT_SLIDE_MASTER,
            }),
          ]
        : []),
      ...(props.customFields.includedSlides.overall.executiveSummary
        ? [
            buildSlide(
              [
                contentSlideHeader('Executive Summary'),
                {
                  text: executiveSummaryTexts.header,
                  options: {
                    h: 0.47,
                    w: 3.77,
                    x: 0.74,
                    y: 1.67,
                    valign: 'top',
                    bold: true,
                  },
                },
                {
                  text: parsedTakeaways(
                    props.customFields.summary.split(NEWLINE_REGEX)
                  ),
                  options: {
                    h: 1.94,
                    w: 11.98,
                    x: 0.74,
                    y: 2.14,
                    valign: 'top',
                    paraSpaceAfter: 5,
                    fontSize: 16,
                  },
                },
              ],
              DEFAULT_SLIDE_MASTER
            ),
          ]
        : []),
      ...(props.customFields.includedSlides.salesAttribution.titleSlide
        ? [sectionTitleSlide('Sales Attribution')]
        : []),
      ...(props.customFields.includedSlides.salesAttribution.summary
        ? [
            salesLiftSummarySlide({
              slideMaster: DEFAULT_SLIDE_MASTER,
              report: data.report,
              iroas: data.roasData.iroas,
              roas: data.roasData.roas,
              excludedColumns: features.cpgSalesRate ? [] : ['totalSalesRate'],
            }),
          ]
        : []),
      ...(props.customFields.includedSlides.salesAttribution.dimensions
        ? [
            dimensionSlide({
              slideMaster: DEFAULT_SLIDE_MASTER,
              title: topPerformerSlideHeader,
              dimensions: [overallDimension, ...topDimensions],
              excludedColumns: dimensionExcludedColumns,
            }),
            dimensionSlide({
              slideMaster: DEFAULT_SLIDE_MASTER,
              title: bottomPerformerSlideHeader,
              dimensions: [overallDimension, ...bottomDimensions],
              excludedColumns: dimensionExcludedColumns,
            }),
          ]
        : []),
      ...(props.customFields.includedSlides.performanceByRetailer.titleSlide
        ? [sectionTitleSlide('Performance By Retailer')]
        : []),
      ...(props.customFields.includedSlides.performanceByRetailer
        .retailerSummary
        ? [
            retailersSummarySlide({
              retailers: data.report.retailers,
              projectedIncrementalSales: data.report.projectedIncrementalSales,
              totalProjectedSales: data.report.totalProjectedSales,
              slideMaster: DEFAULT_SLIDE_MASTER,
              keyTakeaways: props.customFields.retailersSummary.takeaways.map(
                takeaway => takeaway.value
              ),
              recommendation:
                props.customFields.retailersSummary.recommendation,
            }),
          ]
        : []),
      ...(props.customFields.includedSlides.performanceByRetailer
        .performanceByTopTen
        ? performanceByTopRetailersSlides({
            groupedRetailers,
            overallRetailer: buildOverallRetailer(data.report),
            recommendation:
              props.customFields.performanceByRetailer.recommendation,
            excludedColumns: features.cpgSalesRate ? [] : ['salesRate'],
          })
        : []),
      ...(props.customFields.includedSlides.appendix
        ? [
            sectionTitleSlide('Appendix'),
            ...dimensionSlides({
              slideMaster: DEFAULT_SLIDE_MASTER,
              dimensions: data.report.dimensions,
              excludedColumns: features.cpgSalesRate ? [] : ['salesRate'],
              customHeaders: Object.keys(props.customFields.dimensions)
                .filter(slug => validDimensionSlug(slug))
                .reduce(
                  (acc, key) => {
                    const customHeader =
                      props.customFields.dimensions[key].tableHeader;
                    acc[key] =
                      customHeader && customHeader.length > 0
                        ? customHeader
                        : undefined;
                    return acc;
                  },
                  {} as DimensionSlidesProps['customHeaders']
                ),
            }),
            howWeDoItSlide(),
            lciAdaptiveSyntheticControlSlide(),
            multiTouchAttributionSlide(),
            projectedSalesMethodologySlide(),
            minimumsSlide(),
            ...glossarySlides({
              entriesPerColumn: [10, 10, 11],
              glossary: GLOSSARY,
            }),
            calculationsSlide(),
            talkToUsSlide(),
            emailSlide(),
          ]
        : []),
    ],
  };
};

export const buildPresentation = (
  props: BuildPresentationProps
): Presentation => buildPresentationV1(props);
